import { CheckCircle, Music } from 'lucide-react';
import FileInput from '../ui/FileInput';
import { QuestionFormat } from '../../lib/questionPresets';
import { renderRichText } from '../../lib/richText';
import type { OptionForm } from '../../lib/questionForm';

const OPTION_FORMATS: QuestionFormat[] = [
  QuestionFormat.TEXT,
  QuestionFormat.IMAGE,
  QuestionFormat.AUDIO,
];
const LETTERS = ['A', 'B', 'C', 'D'];

// Matches apps/mobile MockTestScreen styles: optBtn (white, radius 10,
// border gray[200]), optSel (border/bg blue when selected), optNum
// (28x28 badge, blue border). Reused here to mean "marked correct" —
// same visual language mobile uses for "this is picked".
const BROWN = '#2D2016';
const BORDER = '#F5E6D0';
const SELECTED_BORDER = '#4FC3F7';
const SELECTED_BG = '#E1F5FE';
const PANEL_BG = '#FFF9E8';

interface QuestionOptionsFieldsProps {
  optionFormat: QuestionFormat;
  onOptionFormatChange: (format: QuestionFormat) => void;
  options: OptionForm[];
  onOptionChange: (index: number, patch: Partial<OptionForm>) => void;
}

export default function QuestionOptionsFields({
  optionFormat,
  onOptionFormatChange,
  options,
  onOptionChange,
}: QuestionOptionsFieldsProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-gray-400">
          Answers (exactly 4, mark 1 correct)
        </span>
        <select
          value={optionFormat}
          onChange={(e) => onOptionFormatChange(e.target.value as QuestionFormat)}
          className="rounded-md border border-gray-200 px-2 py-1 text-xs"
        >
          {OPTION_FORMATS.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </div>

      {options.map((opt, idx) => (
        <div
          key={idx}
          className="flex items-start gap-2.5 rounded-[10px] border-[1.5px] bg-white p-2.5 transition-colors"
          style={
            opt.isCorrect
              ? { borderColor: SELECTED_BORDER, backgroundColor: SELECTED_BG }
              : { borderColor: BORDER }
          }
        >
          <button
            type="button"
            onClick={() => onOptionChange(idx, { isCorrect: true })}
            title="Mark as the correct answer"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] border-[1.5px] text-xs font-bold"
            style={
              opt.isCorrect
                ? { borderColor: SELECTED_BORDER, backgroundColor: SELECTED_BORDER, color: '#fff' }
                : { borderColor: SELECTED_BORDER, color: BROWN }
            }
          >
            {LETTERS[idx]}
          </button>

          <div className="min-w-0 flex-1">
            {optionFormat === QuestionFormat.TEXT && (
              <>
                <input
                  type="text"
                  value={opt.text}
                  onChange={(e) => onOptionChange(idx, { text: e.target.value })}
                  placeholder={`Option ${LETTERS[idx]}`}
                  data-rt-field={`option:${idx}`}
                  className="w-full border-0 bg-transparent p-0 text-sm font-medium outline-none"
                  style={{ color: BROWN }}
                />
                {/[biu]>/.test(opt.text) && (
                  <div className="mt-1 text-sm font-korean" style={{ color: BROWN }}>
                    {renderRichText(opt.text)}
                  </div>
                )}
              </>
            )}
            {optionFormat === QuestionFormat.IMAGE && (
              <div className="space-y-1.5">
                <div
                  className="flex items-center justify-center overflow-hidden rounded-[10px]"
                  style={{ height: 100, backgroundColor: PANEL_BG }}
                >
                  {opt.imageUrl ? (
                    <img
                      src={opt.imageUrl}
                      alt={`Option ${LETTERS[idx]}`}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <span className="text-[11px] text-gray-400">No image yet</span>
                  )}
                </div>
                <FileInput
                  value={opt.imageUrl}
                  onChange={(url) => onOptionChange(idx, { imageUrl: url })}
                  accept="image"
                  placeholder={`Option ${LETTERS[idx]} image`}
                />
              </div>
            )}
            {optionFormat === QuestionFormat.AUDIO && (
              <div className="space-y-1.5">
                <div
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5"
                  style={{ backgroundColor: PANEL_BG }}
                >
                  <Music size={12} color="#4A3728" />
                  <span className="text-[11px] font-semibold" style={{ color: '#4A3728' }}>
                    {opt.audioUrl ? 'Audio attached' : 'No audio yet'}
                  </span>
                </div>
                <FileInput
                  value={opt.audioUrl}
                  onChange={(url) => onOptionChange(idx, { audioUrl: url })}
                  accept="audio"
                  placeholder={`Option ${LETTERS[idx]} audio`}
                />
              </div>
            )}
          </div>

          {opt.isCorrect && (
            <CheckCircle size={16} className="mt-0.5 shrink-0" color={SELECTED_BORDER} />
          )}
        </div>
      ))}
    </div>
  );
}
